import { useState } from "react";
import { Button, Select } from "@mantine/core";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useEmailTemplatesQuery } from "../api/queries/emailTemplateQueries";
import { useGenerateDraftsMutation } from "../api/queries/paymentsQueries";

interface InvoiceTemplateDraftGeneratorProps {
  invoiceId: string;
  hasExistingDraft: boolean;
  disabled?: boolean;
  variant?: "default" | "compact";
  hideLabel?: boolean;
  className?: string;
}

export const InvoiceTemplateDraftGenerator = ({
  invoiceId,
  hasExistingDraft,
  disabled = false,
  variant = "default",
  hideLabel = false,
  className = "",
}: InvoiceTemplateDraftGeneratorProps) => {
  const { data: templates = [], isLoading: isTemplatesLoading } =
    useEmailTemplatesQuery();
  const generateDraftsMutation = useGenerateDraftsMutation();
  const [selectedTemplateId, setSelectedTemplateId] = useState("");

  const enabledTemplates = [...templates]
    .filter((template) => template.enabled)
    .sort((a, b) => a.name.localeCompare(b.name));
  const isGenerating = generateDraftsMutation.isPending;
  const isCompact = variant === "compact";

  const handleGenerate = async () => {
    const templateId = Number(selectedTemplateId);
    if (!templateId) return;

    if (
      hasExistingDraft &&
      !window.confirm(
        "This will replace the current draft with content from the selected template. Continue?",
      )
    ) {
      return;
    }

    const loadingToast = toast.loading("Generating draft from template...");

    try {
      const result = await generateDraftsMutation.mutateAsync({
        payment_external_ids: [invoiceId],
        template_id: templateId,
      });

      toast.dismiss(loadingToast);

      if (result.generated_count > 0) {
        toast.success("Draft generated from template.");
      } else if (result.failed_count > 0) {
        toast.error("Failed to generate draft from template.");
      } else {
        toast.warning("Draft was not generated. The invoice may be ineligible.");
      }
    } catch (error: unknown) {
      toast.dismiss(loadingToast);
      const err = error as {
        response?: { data?: { detail?: unknown } };
        message?: string;
      };
      const detail = err?.response?.data?.detail;
      const errorMessage =
        (typeof detail === "object" &&
        detail !== null &&
        "message" in detail &&
        typeof (detail as { message?: string }).message === "string"
          ? (detail as { message: string }).message
          : typeof detail === "string"
            ? detail
            : null) ||
        err?.message ||
        "Failed to generate draft";
      toast.error(errorMessage);
    }
  };

  if (isTemplatesLoading) {
    if (isCompact) {
      return (
        <div
          className={`flex h-[36px] items-center text-sm text-slate-500 ${className}`}
        >
          Loading templates...
        </div>
      );
    }

    return (
      <div className="mb-4 flex items-center gap-2 text-sm text-slate-500">
        Loading templates...
      </div>
    );
  }

  if (enabledTemplates.length === 0) {
    if (isCompact) {
      return (
        <div
          className={`rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-2 text-xs text-amber-900 ${className}`}
        >
          No enabled templates.{" "}
          <Link to="/settings" className="font-medium underline">
            Settings → Email
          </Link>
        </div>
      );
    }

    return (
      <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
        <p className="font-medium">No enabled email templates</p>
        <p className="mt-0.5 text-amber-800">
          Create and enable a template in{" "}
          <Link to="/settings" className="font-medium underline">
            Settings → Email
          </Link>{" "}
          before generating a draft.
        </p>
      </div>
    );
  }

  if (isCompact) {
    const templateSelect = (
      <div className="flex min-w-0 flex-1 items-center gap-2">
        <Select
          id="invoice-template-select"
          className="min-w-0 flex-1"
          placeholder="Select a template..."
          data={enabledTemplates.map((template) => ({
            value: String(template.id),
            label: template.name,
          }))}
          value={selectedTemplateId || null}
          onChange={(value) => setSelectedTemplateId(value ?? "")}
          disabled={disabled || isGenerating}
          searchable={false}
          radius="md"
          comboboxProps={{ withinPortal: true }}
        />
        <Button
          variant="filled"
          radius="md"
          onClick={handleGenerate}
          disabled={disabled || isGenerating || !selectedTemplateId}
          loading={isGenerating}
          className="shrink-0"
        >
          Generate
        </Button>
      </div>
    );

    if (hideLabel) {
      return <div className={className}>{templateSelect}</div>;
    }

    return (
      <div className={`w-full ${className}`}>
        <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">
          Template
        </p>
        {templateSelect}
      </div>
    );
  }

  return (
    <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50/80 p-4">
      <label
        htmlFor="invoice-template-select-default"
        className="block text-sm font-medium text-slate-700"
      >
        Email template
      </label>
      <div className="mt-1.5 flex flex-col gap-3 sm:flex-row sm:items-center">
        <Select
          id="invoice-template-select-default"
          className="min-w-0 flex-1"
          placeholder="Select a template..."
          data={enabledTemplates.map((template) => ({
            value: String(template.id),
            label: template.name,
          }))}
          value={selectedTemplateId || null}
          onChange={(value) => setSelectedTemplateId(value ?? "")}
          disabled={disabled || isGenerating}
          searchable={false}
          radius="md"
          comboboxProps={{ withinPortal: true }}
        />
        <Button
          variant="filled"
          radius="md"
          onClick={handleGenerate}
          disabled={disabled || isGenerating || !selectedTemplateId}
          loading={isGenerating}
          className="shrink-0 sm:w-auto"
        >
          Generate from template
        </Button>
      </div>
      <p className="mt-1.5 text-xs text-slate-500">
        Generate a draft from a saved template. You can review and edit it
        before sending.
      </p>
    </div>
  );
};
